import React, { Fragment, useEffect, useState } from 'react'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { GenericTable } from '../../components/_EOS/Table'
import { Courier } from '../../interfaces/Courier'
import { useApi } from '../../hooks/useApi'
import { ApiResponse } from '../../typings/ApiResponse'
import { Modal } from '../../components/_EOS/Modal'
import { useModal } from '../../hooks/useModal'
import { usePagination } from '../../hooks/usePagination'
import { CourierForm } from './form/CourierForm'
import { CourierFormEdit } from './form'
import { Status } from '../../hooks/useFetchState'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { useSnackbar } from '../../hooks/useSnackbar'
import { Typography } from '@mui/material'
import { useTypesPriority } from '../../hooks/useTypesPriority'
import { FilterComponent, Filter } from '../../components/_WMS/FilterComponent'
import { filterOptions } from './config/filterOptions'
import { getCouriers, deleteCourier } from './config/api'

interface CourierPageProps {}

export const CourierPage: React.FC<CourierPageProps> = () => {
  const { state, callApi } = useApi<ApiResponse<Courier>>()
  const { state: deleteCourierState, callApi: deleteCourierCallApi } =
    useApi<ApiResponse<Courier>>()

  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([])

  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()

  const editModal = useModal<{ courierId: number }>('Editar courier')
  const createModal = useModal('Crear courier')
  const deleteModal = useModal<{ courier: Courier }>('Eliminar courier')

  const pagination = usePagination(5)

  const requestCouriers = (
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => {
    callApi(getCouriers(filters, page, pageSize)).catch((err) =>
      openSnackBar(err, false)
    )
  }

  const requestRemoveCourier = (courierId: number) => {
    deleteCourierCallApi(deleteCourier(courierId))
      .then(() => openSnackBar('Courier eliminado correctamente', true))
      .catch((err) => openSnackBar(err, false))
      .finally(() => {
        deleteModal.closeModal()
        requestCouriers(selectedFilters, pagination.page, pagination.pageSize)
      })
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    requestCouriers(selectedFilters, auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  const closeCreateCourierModal = (success?: boolean) => {
    if (success) {
      createModal.closeModal()
      requestCouriers(selectedFilters, pagination.page, pagination.pageSize)
    }
  }

  const handleDeleteModal = deleteModal.extendedProps?.courier
    ? () => {
        requestRemoveCourier(deleteModal.extendedProps?.courier.courierId || 0)
      }
    : () => {}

  const {
    getTypesPriority,
    typesPriorityState: { typesPriority }
  } = useTypesPriority()

  useEffect(() => {
    if (!typesPriority || typesPriority?.length <= 0) {
      getTypesPriority()
    }
    requestCouriers(selectedFilters, 0, pagination.pageSize)
  }, [])

  return (
    <Fragment>
      <StandardPage pageTitle=''>
        <FilterComponent
          options={filterOptions}
          onSearchClick={(filters) => {
            pagination.setPagination(0, pagination.pageSize)
            requestCouriers(filters, 0, pagination.pageSize)
          }}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          includeAddButton={true}
          onAddButtonClick={() => createModal.openModal('md')}
          addButtonTitle='Crear'
          includeSearchOnClean={true}
          createButtonTooltip='Crear Courier'
        />
        <GenericTable<Courier>
          headersConfig={[
            { field: 'courierId', title: 'ID' },
            { field: 'name', title: 'Nombre' }
          ]}
          IDKey={'courierId'}
          data={state.data?.items}
          actions={{
            onEdit: (id) => editModal.openModal('lg', { courierId: id }),
            onDelete: (_id, courier) => deleteModal.openModal('sm', { courier })
          }}
          pagination={{
            totalItem: state.data?.totalItems || 0,
            page: pagination.page,
            pageSize: pagination.pageSize
          }}
          onPaginationChange={handlePaginationChange}
          isLoading={state.status === Status.Loading}
        />
      </StandardPage>

      <Modal
        open={editModal.isOpen}
        maxWidth={editModal.size}
        onClose={editModal.closeModal}
        title={editModal.title}
      >
        {editModal.extendedProps && (
          <CourierFormEdit
            courierId={editModal.extendedProps?.courierId}
            handleSnackbar={openSnackBar}
          />
        )}
      </Modal>
      <Modal
        open={createModal.isOpen}
        maxWidth={createModal.size}
        onClose={createModal.closeModal}
        title={createModal.title}
      >
        <CourierForm
          onCreate={closeCreateCourierModal}
          handleSnackbar={openSnackBar}
        />
      </Modal>
      <Modal
        open={deleteModal.isOpen}
        maxWidth={deleteModal.size}
        onClose={deleteModal.closeModal}
        title={deleteModal.title}
        loading={deleteCourierState.status === Status.Loading}
        cancelButton={{
          action: () => deleteModal.closeModal(),
          title: 'Cancelar'
        }}
        successButton={{
          action: handleDeleteModal,
          title: 'Eliminar'
        }}
      >
        <Typography variant='body2' component={'h4'}>
          ¿Está seguro que desea eliminar el courier{' '}
          {deleteModal.extendedProps?.courier.name}?
        </Typography>
      </Modal>

      <SnackBar
        message={message}
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
      />
    </Fragment>
  )
}
